import './TrialFontsForm.scss';

import type { FC } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation, Trans } from 'react-i18next';
import { Formik } from 'formik';
import { gql, useMutation, useReactiveVar } from '@apollo/client';

import {
  RequestTrialFontsMutation,
  RequestTrialFontsMutationVariables,
} from 'client/graphql/types/operations';

import * as state from 'state';
import * as Core from 'components/core';

const TRANSITION_DURATION = 300;

const REQUEST_TRIAL_FONTS_MUTATION = gql`
  mutation RequestTrialFontsMutation($input: RequestTrialFontsInput!) {
    requestTrialFonts(input: $input) {
      success
    }
  }
`;

interface Props {}

const TrialFontsForm: FC<Props> = () => {
  const { t } = useTranslation(['translation', 'checkout']);

  const visible = useReactiveVar(state.trialFontsFormVisible);

  const [requestTrialFonts, { loading }] = useMutation<
    RequestTrialFontsMutation,
    RequestTrialFontsMutationVariables
  >(REQUEST_TRIAL_FONTS_MUTATION);

  return (
    <AnimateHeight height={visible ? 'auto' : 0} duration={TRANSITION_DURATION}>
      <Formik
        initialValues={{
          email: '',
          receiveNewsletter: false,
          agreeToLicense: false,
          agreeToTermsAndConditions: false,
        }}
        onSubmit={(values) => {
          requestTrialFonts({
            variables: {
              input: {
                email: values.email,
                receiveNewsletter: values.receiveNewsletter,
              },
            },
          }).then((result) => {
            if (result.data?.requestTrialFonts?.success) {
              state.toggleTrialFontsForm();

              state.appendNotification({
                type: 'notice',
                content: t('trialFontsRequest.successMessage'),
              });
            } else {
              state.appendNotification({
                type: 'alert',
                content: t('trialFontsRequest.errorMessage'),
              });
            }
          });
        }}
      >
        {({ handleSubmit, handleChange, handleBlur, values }) => (
          <form className="FamiliesTrialFontsForm" onSubmit={handleSubmit}>
            <button
              className="FamiliesTrialFontsForm-content-button"
              type="button"
              onClick={() => {
                state.toggleTrialFontsForm();
              }}
            >
              &times;
            </button>

            <div className="FamiliesTrialFontsForm-content content-wrapper">
              <div className="FamiliesTrialFontsForm-content-column">
                <h3 className="FamiliesTrialFontsForm-content-column-heading">
                  {t('trialFontsRequest.heading')}
                </h3>

                <p>{t('trialFontsRequest.explanation')}</p>
              </div>

              <div className="FamiliesTrialFontsForm-content-column">
                <Core.FormGroup label={t('user.email')} required>
                  {({ inputID }) => (
                    <div className="FamiliesTrialFontsForm-content-column-input">
                      <Core.Input
                        name="email"
                        autoComplete="email"
                        required
                        id={inputID}
                        disabled={loading}
                        placeholder="email address"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />

                      <Core.Button
                        type="submit"
                        disabled={
                          loading ||
                          values.email.length === 0 ||
                          !values.agreeToLicense ||
                          !values.agreeToTermsAndConditions
                        }
                      >
                        {t('trialFontsRequest.request')}
                      </Core.Button>
                    </div>
                  )}
                </Core.FormGroup>

                <Core.Checkbox
                  name="agreeToLicense"
                  disabled={loading}
                  checked={values.agreeToLicense}
                  testID="terms-and-conditions-checkbox"
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <Trans
                    ns="checkout"
                    i18nKey="payment.agreeToTerms"
                    values={{
                      licenseType: t(`licenseType.trial.fullName`),
                    }}
                  >
                    <Core.Link to="/licensing#trial" target="_blank">
                      {t('pages.termsAndConditions')}
                    </Core.Link>
                  </Trans>
                </Core.Checkbox>

                <Core.Checkbox
                  name="agreeToTermsAndConditions"
                  disabled={loading}
                  checked={values.agreeToTermsAndConditions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  testID="terms-and-conditions-checkbox"
                >
                  <Trans
                    ns="checkout"
                    i18nKey="payment.agreeToTermsAndConditions"
                  >
                    <Core.Link to="/terms-and-conditions" target="_blank">
                      {t('pages.termsAndConditions')}
                    </Core.Link>
                  </Trans>
                </Core.Checkbox>

                <Core.Checkbox
                  name="receiveNewsletter"
                  checked={values.receiveNewsletter}
                  disabled={loading}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {t('trialFontsRequest.newsletterSignUp')}
                </Core.Checkbox>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </AnimateHeight>
  );
};

export default TrialFontsForm;
